import { useContext, useMemo, useRef, useState } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { authenticateBySignature, transferFromWeb3 } from '../utils/solana.utils';
import * as web3 from '@solana/web3.js';
import { Id, toast } from 'react-toastify';
import { LedgerSignatureModel, UserProviderValue } from '../models';
import { authenticate, authenticateLedger, getUserAuthToken, linkWeb3LedgerWallet, linkWeb3Wallet } from '../services';
import { UserContext } from '../contexts/user.context';
import { useSearchParams } from 'react-router-dom';

export const useLoginWeb3 = () => {
   const { isAuthenticated, setIsAuthenticated, setUser, setIsLoginModalOpen } = useContext<UserProviderValue>(UserContext);
   const [searchParams] = useSearchParams();
   const referralCode = searchParams.get('referral');

   const [authRequested, setAuthRequested] = useState(false);
   const [isLedger, setIsLedger] = useState(false);
   const { connection } = useConnection();
   const toastId = useRef<Id>(-1);
   const [loading, setLoading] = useState(false);

   const [ledgerExpanded, setLedgerExpanded] = useState(false);
   const wallet = useWallet();

   const showWalletConfirmModal = useMemo(() => {
      return wallet?.publicKey && authRequested;
   }, [wallet, authRequested]);

   const disconnectWallet = () => {
      wallet?.disconnect();
      setAuthRequested(false);
   };

   const authenticateUser = async () => {
      if (!wallet?.publicKey) return;
      try {
         setLoading(true);
         toastId.current = toast.loading('Authenticating...', {
            position: 'bottom-left',
            autoClose: false,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
         });

         const authToken = await getUserAuthToken();
         if (!authToken) {
            toast.error('Auth service encountered an error, please try again.');
            return;
         }

         if (isLedger) {
            const newTransaction = new web3.Transaction();
            var enc = new TextEncoder();
            newTransaction.add(
               transferFromWeb3({
                  fromPubkey: wallet.publicKey,
                  toPubkey: wallet.publicKey,
                  lamports: (web3.LAMPORTS_PER_SOL / 1000000) * 5,
                  token: enc.encode(authToken.data.token),
               })
            );

            newTransaction.feePayer = wallet.publicKey;
            const latestBlockHash = await connection.getLatestBlockhash();
            newTransaction.recentBlockhash = latestBlockHash.blockhash;
            const ledgerTransaction = await wallet.sendTransaction(newTransaction, connection);

            await connection.confirmTransaction(
               {
                  blockhash: latestBlockHash.blockhash,
                  lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
                  signature: ledgerTransaction,
               },
               'confirmed'
            );

            const ledgerSignedTransaction: LedgerSignatureModel = {
               signer: wallet.publicKey.toString(),
               signature: ledgerTransaction,
               tokenId: authToken.data.id,
               referralCode: referralCode ?? undefined,
            };

            const result = await (isAuthenticated ? linkWeb3LedgerWallet(ledgerSignedTransaction) : authenticateLedger(ledgerSignedTransaction));
            if (result) {
               setIsAuthenticated(true);
               setAuthRequested(false);
               //   refreshUserAccount();
               setIsLoginModalOpen(false);

               setUser(result.data);

               toast.update(toastId.current, {
                  type: toast.TYPE.SUCCESS,
                  render: 'Authenticated successfully',
                  autoClose: 1000,
                  closeOnClick: true,
                  isLoading: false,
               });
            }
         } else {
            const signature = await authenticateBySignature(
               `Wallet confirmation ⛳ \n\nI have read and agreed to the Terms and Conditions.\n\nNo amount will be charged.\n\nSession: ${authToken.data.token}`,
               wallet,
               authToken.data.id
            );
            if (!signature) {
               toast.update(toastId.current, {
                  type: toast.TYPE.ERROR,
                  render: "The signature couldn't be verified, please try again.",
                  autoClose: 10000,
                  closeOnClick: true,
                  isLoading: false,
               });
               setLoading(false);

               return;
            }

            signature.referralCode = referralCode ?? undefined;

            const result = await (isAuthenticated ? linkWeb3Wallet(signature) : authenticate(signature));
            if (result) {
               if (result.error || !result.data) {
                  toast.update(toastId.current, {
                     type: toast.TYPE.ERROR,
                     render: result.error?.message ?? "The signature couldn't be verified, please try again.",
                     autoClose: 10000,
                     closeOnClick: true,
                     isLoading: false,
                  });
                  return;
               }
               if (!isAuthenticated) setIsAuthenticated(true);
               setAuthRequested(false);

               setIsLoginModalOpen(false);

               setUser(result.data);

               toast.update(toastId.current, {
                  type: toast.TYPE.SUCCESS,
                  render: 'Authenticated successfully',
                  autoClose: 1000,
                  closeOnClick: true,
                  isLoading: false,
               });
            } else {
               toast.update(toastId.current, {
                  type: toast.TYPE.ERROR,
                  render: "The signature couldn't be verified, please try again.",
                  autoClose: 10000,
                  closeOnClick: true,
                  isLoading: false,
               });
            }
         }
         setLoading(false);
      } catch (e) {
         console.error(e);
         setLoading(false);
         toast.done(toastId.current);
      } finally {
         setLoading(false);
         setIsLedger(false);
         setLedgerExpanded(false);
      }
   };

   return {
      isAuthenticated,
      isLedger,
      ledgerExpanded,
      loading,
      wallet,
      setIsLedger,
      setLedgerExpanded,
      authenticateUser,
      disconnectWallet,
      showWalletConfirmModal,
      setAuthRequested,
   };
};
